/**
 * When the popup may run (merchant-local times via datetime-local).
 * Keep in sync with the WeUpsell admin `client/helper/displaySchedule.js`.
 * @typedef {{ mode?: string, startAt?: string, endAt?: string }} DisplaySchedule
 */

const MODES = {
  ALWAYS: "always",
  FROM_START: "from_start",
  START_TO_END: "start_to_end",
};

export function normalizeDisplaySchedule(condition) {
  const raw = condition?.displaySchedule;
  if (raw && typeof raw.mode === "string") {
    const m = String(raw.mode).trim();
    const validMode = [MODES.ALWAYS, MODES.FROM_START, MODES.START_TO_END].includes(m)
      ? m
      : MODES.ALWAYS;
    return {
      mode: validMode,
      startAt: raw.startAt != null ? String(raw.startAt) : "",
      endAt: raw.endAt != null ? String(raw.endAt) : "",
    };
  }

  const t = condition?.timer;
  if (t?.isTimerPopup && t?.eventStartTime && t?.eventEndTime) {
    return {
      mode: MODES.START_TO_END,
      startAt: String(t.eventStartTime),
      endAt: String(t.eventEndTime),
    };
  }
  if (t?.isTimerPopup && t?.eventStartTime) {
    return {
      mode: MODES.FROM_START,
      startAt: String(t.eventStartTime),
      endAt: t.eventEndTime != null ? String(t.eventEndTime) : "",
    };
  }

  return { mode: MODES.ALWAYS, startAt: "", endAt: "" };
}

/**
 * @param {DisplaySchedule | ReturnType<typeof normalizeDisplaySchedule>} schedule
 * @returns {boolean}
 */
export function isWithinDisplaySchedule(schedule) {
  const mode = schedule?.mode || MODES.ALWAYS;
  if (mode === MODES.ALWAYS) return true;

  const now = Date.now();
  const startMs = schedule.startAt ? new Date(schedule.startAt).getTime() : NaN;
  const endMs = schedule.endAt ? new Date(schedule.endAt).getTime() : NaN;

  if (mode === MODES.FROM_START) {
    if (!Number.isFinite(startMs)) return false;
    return now >= startMs;
  }

  if (mode === MODES.START_TO_END) {
    if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) return false;
    if (endMs <= startMs) return false;
    return now >= startMs && now <= endMs;
  }

  return true;
}

/**
 * @param {DisplaySchedule} schedule
 * @returns {{ ok: boolean, message?: string }}
 */
export function validateDisplaySchedule(schedule) {
  const mode = schedule?.mode || MODES.ALWAYS;
  if (mode === MODES.ALWAYS) return { ok: true };

  const startMs = schedule.startAt ? new Date(schedule.startAt).getTime() : NaN;
  const endMs = schedule.endAt ? new Date(schedule.endAt).getTime() : NaN;

  if (mode === MODES.FROM_START) {
    if (!schedule.startAt || !String(schedule.startAt).trim()) {
      return { ok: false, message: "Select a start date and time." };
    }
    if (!Number.isFinite(startMs)) {
      return { ok: false, message: "Start date is not valid." };
    }
    return { ok: true };
  }

  if (mode === MODES.START_TO_END) {
    if (!schedule.startAt || !String(schedule.startAt).trim()) {
      return { ok: false, message: "Select a start date and time." };
    }
    if (!schedule.endAt || !String(schedule.endAt).trim()) {
      return { ok: false, message: "Select an end date and time." };
    }
    if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) {
      return { ok: false, message: "Start or end date is not valid." };
    }
    if (endMs <= startMs) {
      return { ok: false, message: "End must be after start." };
    }
    return { ok: true };
  }

  return { ok: true };
}

export const DISPLAY_SCHEDULE_MODES = MODES;
